// --- React & Redux
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// --- Hooks & Actions
import { logout } from "tap/store/auth";
// --- External UI Libraries
import LottiePlayer from "@dtci/spellbook/LottiePlayer";
// --- Components
import { Header } from "../../components/Header";
// --- Config
import { ENDPOINTS_KEYS } from "../../config/constants";
import myIDLogo from "../../../shared/assets/img/myid.png";
// --- Styles
import styles from "./login.module.scss";

/**
 * Login Component
 *
 * This component serves as the login page for the application.
 * It includes a brand logo, a title, and a login button.
 * It also handles error states, loading indicators, and auth verification states.
 *
 * @component
 * @param {Object} props
 * @param {"login"|"verifying"} props.mode - The mode of the login page (default: "login")
 * @returns {JSX.Element} The login page with branding and login functionality.
 **/
export const Login = ({ mode = "login" }) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const reduxError = useSelector((s) => s.auth.error);
  const { showSpinner } = useSelector((s) => s.spinner);
  const [isStartingLogin, setIsStartingLogin] = useState(false);
  const [postLoginRedirect, setPostLoginRedirect] = useState("");

  const urlError = searchParams.get("error");
  const hasError = Boolean(reduxError) || Boolean(urlError);
  const errorMessage = reduxError || urlError || "";
  const isVerifying = mode === "verifying";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("postLoginRedirect");
    const tokenFromUrl = params.get("token");
    setPostLoginRedirect(redirect || "");
    if (redirect) {
      params.delete("postLoginRedirect");
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "");
      window.history.replaceState({}, "", newUrl);
    } else if (!tokenFromUrl) {
      localStorage.removeItem("postLoginRedirect");
    }
  }, []);

  async function handleMyIdLogin(event) {
    event.preventDefault();
    if (isStartingLogin) {
      return;
    }
    setIsStartingLogin(true);
    try {
      await dispatch(logout());
    } catch {
      // Thunk handles API errors; still proceed to SSO
    }
    window.location.assign(ENDPOINTS_KEYS.login);
  }

  let showContinueToApiRedirect = false;
  let decodedPostLoginRedirect = "";
  if (postLoginRedirect) {
    try {
      decodedPostLoginRedirect = decodeURIComponent(postLoginRedirect);
      showContinueToApiRedirect = decodedPostLoginRedirect.includes("/api");
    } catch {
      decodedPostLoginRedirect = postLoginRedirect;
      showContinueToApiRedirect = postLoginRedirect.includes("/api");
    }
  }

  return (
    <>
      <Header />
      <div className={styles.root}>
        <div className={styles.leftPanel}>
          <div className={styles.bgImg} />
        </div>
        <div className={styles.rightPanel}>
          <div className={styles.content}>
            {isVerifying ? (
              <>
                <h1 className={styles.title}>Verifying Authentication</h1>
                <div className={styles.divider}>
                  <hr />
                  <span>Please wait</span>
                  <hr />
                </div>
                <LottiePlayer
                  style={{
                    position: "relative",
                    maxWidth: "120px",
                    marginInline: "auto",
                  }}
                />
              </>
            ) : (
              <>
                <h1 className={styles.title} data-error={hasError}>
                  {hasError ? "403 Unauthorized" : "Login to TAP UI"}
                </h1>
                {showContinueToApiRedirect && (
                  <div
                    style={{
                      marginBottom: 8,
                      color: "#666",
                      wordBreak: "break-all",
                      fontSize: 14,
                    }}
                  >
                    To continue to{" "}
                    <span style={{ fontWeight: 500 }}>
                      {decodedPostLoginRedirect}
                    </span>
                  </div>
                )}
                <div className={styles.divider} data-error={hasError}>
                  <hr />
                  <span>
                    {hasError ? "You are not authorized" : "Continue with"}
                  </span>
                  <hr />
                </div>
                {showSpinner || isStartingLogin ? (
                  <LottiePlayer
                    style={{
                      position: "relative",
                      maxWidth: "120px",
                      marginInline: "auto",
                    }}
                  />
                ) : (
                  <>
                    {hasError && <p className={styles.error}>{errorMessage}</p>}
                    {!hasError && (
                      <a
                        href={ENDPOINTS_KEYS.login}
                        className={styles.myID}
                        onClick={handleMyIdLogin}
                      >
                        Login with
                        <img src={myIDLogo} alt="Disney MyID" />
                      </a>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
